import { useEffect } from "react";
import { Maximize2, UserCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { trpc } from "@/lib/trpc";
import { PlanoPagoSalas } from "@/components/salas/PlanoPagoSalas";
import { ListaAgora } from "@/components/salas/ListaAgora";
import { PlantaAoVivo } from "@/components/salas/planta/PlantaAoVivo";
import { useSalasAgora } from "@/components/salas/use-salas-agora";
import { quem } from "@/components/salas/estado-na-tela";

const minutos = (h: string) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));

/** Tela cheia pro monitor do balcão: a planta ao vivo e quem espera, só com as iniciais do paciente. */
export function Recepcao() {
  const { user } = useAuth();
  const podeVer = usePermissao("agenda.view");
  const s = useSalasAgora({ recepcao: true });
  const planta = trpc.planta.obter.useQuery(undefined, { enabled: s.temRecurso, refetchInterval: 5 * 60_000 });

  // Monitor de balcão não deve apagar sozinho. Sem suporte (ou negado), segue normal.
  useEffect(() => {
    let trava: { release: () => Promise<void> } | null = null;
    const pedir = () => (navigator as any).wakeLock?.request("screen").then((t: any) => (trava = t)).catch(() => {});
    pedir();
    const aoVoltar = () => document.visibilityState === "visible" && pedir();
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      document.removeEventListener("visibilitychange", aoVoltar);
      trava?.release().catch(() => {});
    };
  }, []);

  if (!s.temRecurso) return <Moldura><PlanoPagoSalas /></Moldura>;
  if (!podeVer) return <Moldura><p className="text-center text-sm text-zinc-500">O modo recepção é para quem vê a agenda da clínica.</p></Moldura>;

  const esperando = s.consultas.filter((c) => c.status === "WAITING").sort((a, b) => a.startTime.localeCompare(b.startTime));
  const proximos = s.consultas.filter(
    (c) => ["SCHEDULED", "CONFIRMED"].includes(c.status) && c.startTime > s.agora && minutos(c.startTime) - minutos(s.agora) <= 60,
  );
  const nomeDaSala = (id: string | null) => s.salas.find((x) => x.id === id)?.nome ?? "Sem sala";
  const temPlanta = !!planta.data?.salas.length;

  return (
    <div className="flex min-h-dvh flex-col gap-4 bg-zinc-50 p-4 sm:p-6 dark:bg-background">
      <header className="flex items-center gap-4">
        <h1 className="flex-1 truncate text-xl font-bold text-zinc-900 dark:text-foreground">{user?.activeOrganization?.name}</h1>
        <span className="text-3xl font-bold tabular-nums text-zinc-900 dark:text-foreground">{s.agora}</span>
        <button
          type="button"
          onClick={() => document.documentElement.requestFullscreen?.().catch(() => {})}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[13px] font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground"
        >
          <Maximize2 className="h-4 w-4" />
          Tela cheia
        </button>
      </header>
      <div className="grid flex-1 items-start gap-4 xl:grid-cols-[1fr_320px]">
        {temPlanta ? (
          // Desconta o cabeçalho da tela: no monitor do balcão a planta inteira fica à vista.
          <PlantaAoVivo planta={planta.data!} salas={s.salas} porSala={s.porSala} grande alturaMaxima="calc(100dvh - 7rem)" />
        ) : (
          <ListaAgora salas={s.salas} porSala={s.porSala} semSala={s.semSala} />
        )}
        <aside className="space-y-4">
          <Fila titulo="Esperando" vazio="Ninguém esperando." itens={esperando.map((c) => ({ id: c.id, quem: quem(c), detalhe: `${c.startTime} · ${nomeDaSala(c.salaId)}`, chegou: true }))} />
          <Fila titulo="Próximos 60 minutos" vazio="Nada na próxima hora." itens={proximos.map((c) => ({ id: c.id, quem: quem(c), detalhe: `${c.startTime} · ${nomeDaSala(c.salaId)}` }))} />
        </aside>
      </div>
    </div>
  );
}

function Fila({ titulo, vazio, itens }: { titulo: string; vazio: string; itens: { id: string; quem: string; detalhe: string; chegou?: boolean }[] }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">{titulo}</h2>
      {itens.length ? (
        <ul className="mt-2 divide-y divide-zinc-100 dark:divide-border">
          {itens.map((i) => (
            <li key={i.id} className="flex items-center gap-2 py-2">
              {i.chegou && <UserCheck className="h-4 w-4 shrink-0 text-emerald-600" />}
              <span className="text-lg font-semibold text-zinc-900 dark:text-foreground">{i.quem}</span>
              <span className="ml-auto text-sm tabular-nums text-zinc-500 dark:text-muted-foreground">{i.detalhe}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-zinc-500 dark:text-muted-foreground">{vazio}</p>
      )}
    </section>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center bg-zinc-50 p-6 dark:bg-background"><div className="w-full max-w-3xl">{children}</div></div>;
}
